import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock, FileText, Search, X } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { useReveal } from "@/hooks/useReveal";
import { CATEGORIES, posts, type BlogCategory } from "@/lib/blog";
import { LINK_RULES } from "@/lib/blogLinks";
import { getAuthorForSlug } from "@/lib/authors";

type SortKey = "newest" | "oldest" | "relevance";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "Neueste zuerst" },
  { key: "oldest", label: "Älteste zuerst" },
  { key: "relevance", label: "Relevanz" },
];

// Slug-Mapping für URL-Parameter (lowercase, ASCII).
const CATEGORY_SLUGS: Record<BlogCategory, string> = {
  Steueroptimierung: "steueroptimierung",
  Altersvorsorge: "altersvorsorge",
  Investments: "investments",
  Förderungen: "foerderungen",
  Vermögensaufbau: "vermoegensaufbau",
  Versicherung: "versicherung",
};
const SLUG_TO_CATEGORY: Record<string, BlogCategory> = Object.fromEntries(
  Object.entries(CATEGORY_SLUGS).map(([k, v]) => [v, k as BlogCategory]),
) as Record<string, BlogCategory>;

// Inbound-Link-Score: zählt, wie viele Regeln in blogLinks.ts auf den Slug zeigen.
// Artikel, auf die häufig intern verlinkt wird, gelten als "relevanter".
const INBOUND_SCORE: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  for (const rule of LINK_RULES) {
    if (rule.external) continue;
    const slug = rule.href.replace(/^\/blog\//, "");
    map[slug] = (map[slug] ?? 0) + 1;
  }
  return map;
})();

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();

  const categoryParam = searchParams.get("category");
  const filter: "Alle" | BlogCategory =
    categoryParam && SLUG_TO_CATEGORY[categoryParam]
      ? SLUG_TO_CATEGORY[categoryParam]
      : "Alle";

  const sortParam = searchParams.get("sort") as SortKey | null;
  const sort: SortKey =
    sortParam === "oldest" || sortParam === "relevance" ? sortParam : "newest";

  const query = searchParams.get("q") ?? "";

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const setFilter = (cat: "Alle" | BlogCategory) => {
    updateParams({ category: cat === "Alle" ? null : CATEGORY_SLUGS[cat] });
  };
  const setSort = (s: SortKey) => {
    updateParams({ sort: s === "newest" ? null : s });
  };
  const setQuery = (q: string) => {
    updateParams({ q: q.trim() === "" ? null : q });
  };

  const filtered = useMemo(() => {
    let list = filter === "Alle" ? posts : posts.filter((p) => p.category === filter);

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.teaser.toLowerCase().includes(q),
      );
    }

    const sorted = [...list];
    if (sort === "newest") {
      sorted.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    } else if (sort === "oldest") {
      sorted.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    } else {
      // Relevanz: inbound-Links DESC, dann alphabetisch nach Titel.
      sorted.sort((a, b) => {
        const sa = INBOUND_SCORE[a.slug] ?? 0;
        const sb = INBOUND_SCORE[b.slug] ?? 0;
        if (sb !== sa) return sb - sa;
        return a.title.localeCompare(b.title, "de");
      });
    }
    return sorted;
  }, [filter, query, sort]);

  // Reveal-Observer neu starten, wenn sich Filter/Sortierung/Suche ändern.
  useReveal(`${filter}|${sort}|${query}`);

  // Beim Filter-Wechsel an den Anfang der Liste scrollen wäre overkill — wir lassen es.
  useEffect(() => {}, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog – Finanzwissen, das sich auszahlt | FINABLE"
        description="Steuern sparen, Förderungen nutzen, Vermögen aufbauen – verständliche Artikel zu Altersvorsorge, ETF-Investments und steueroptimierten Strategien."
        canonical="https://finable.de/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "FINABLE Blog",
          url: "https://finable.de/blog",
          description:
            "Finanzwissen zu Altersvorsorge, Steuern, Förderungen und ETF-Investments – verständlich erklärt von FINABLE.",
          inLanguage: "de-DE",
          publisher: {
            "@type": "Organization",
            name: "FINABLE",
            url: "https://finable.de",
            logo: {
              "@type": "ImageObject",
              url: "https://finable.de/logo.png",
            },
          },
          blogPost: posts.map((p) => {
            const author = getAuthorForSlug(p.slug);
            return {
              "@type": "BlogPosting",
              headline: p.title,
              url: `https://finable.de/blog/${p.slug}`,
              datePublished: p.date,
              ...(p.dateModified ? { dateModified: p.dateModified } : {}),
              author: { "@type": "Person", name: author.name },
              publisher: {
                "@type": "Organization",
                name: "FINABLE",
                url: "https://finable.de",
                logo: {
                  "@type": "ImageObject",
                  url: "https://finable.de/logo.png",
                },
              },
            };
          }),
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative text-center max-w-3xl">
            <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
              FINABLE Blog
            </span>
            <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
              Finanzwissen, das sich <span className="text-accent">auszahlt</span>
            </h1>
            <p className="reveal mt-6 text-lg md:text-xl text-white/80 leading-relaxed">
              Steuern sparen, Förderungen nutzen, Vermögen aufbauen – verständlich erklärt.
            </p>
          </div>
        </section>

        {/* Controls + Grid */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            {/* Search */}
            <div className="reveal max-w-xl mx-auto mb-8">
              <label htmlFor="blog-search" className="sr-only">
                Artikel durchsuchen
              </label>
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="blog-search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nach Titel oder Stichwort suchen…"
                  className="w-full h-12 pl-12 pr-12 rounded-full bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 transition appearance-none [&::-webkit-search-cancel-button]:hidden"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Suche zurücksetzen"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category filter (horizontal scroll on mobile, wrap on desktop) */}
            <div className="reveal -mx-4 md:mx-0 mb-4">
              <div className="flex md:flex-wrap md:justify-center gap-2 md:gap-3 overflow-x-auto px-4 md:px-0 pb-2 md:pb-0 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {CATEGORIES.map((cat) => {
                  const active = filter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setFilter(cat)}
                      className={`shrink-0 snap-start px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                        active
                          ? "bg-accent text-accent-foreground border-accent shadow-elegant"
                          : "bg-card text-foreground/70 border-border hover:border-accent/40 hover:text-accent"
                      }`}
                      aria-pressed={active}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sort + results count */}
            <div className="reveal flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
                Artikel gefunden
              </p>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {SORTS.map((s) => {
                  const active = sort === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setSort(s.key)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-all ${
                        active
                          ? "bg-accent text-accent-foreground border-accent shadow-elegant"
                          : "bg-card text-foreground/60 border-border hover:border-accent/40 hover:text-accent"
                      }`}
                      aria-pressed={active}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((post) => (
                <Link
                  key={`${filter}-${sort}-${post.slug}`}
                  to={`/blog/${post.slug}`}
                  className="reveal group flex flex-col bg-card border border-border rounded-2xl overflow-hidden hover:shadow-elegant hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {post.coverImage ? (
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-hero" />
                        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <FileText className="w-14 h-14 text-white/30" />
                        </div>
                      </>
                    )}
                    <span className="absolute top-4 left-4 inline-flex items-center justify-center px-3 h-7 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-[0.12em]">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h2 className="font-display text-xl font-bold text-primary leading-snug group-hover:text-accent transition-colors">
                      {post.title}
                    </h2>
                    <p className="mt-3 text-muted-foreground leading-relaxed text-sm flex-1">
                      {post.teaser}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="w-3.5 h-3.5" />
                        {post.readTime}
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent group-hover:gap-2 transition-all">
                        Weiterlesen <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center mt-12">
                <p className="text-muted-foreground">
                  Keine Artikel gefunden. Probier eine andere Kategorie oder Suche.
                </p>
                <button
                  onClick={() => {
                    setFilter("Alle");
                    setQuery("");
                  }}
                  className="mt-4 inline-flex items-center gap-2 text-accent font-semibold hover:underline"
                >
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
